export const ACQUISITION_TTL = 30 * 24 * 60 * 60 * 1000;

export interface AcquisitionTouch {
   utm: Record<string, string>;
   click_ids: Record<string, string>;
   landing_path: string;
   referrer_host: string;
   captured_at: number;
}

export interface AcquisitionAttribution {
   first_touch?: AcquisitionTouch;
   last_touch?: AcquisitionTouch;
}

export type AcquisitionStep =
   | 'coverage_started'
   | 'email_captured'
   | 'therapy_type'
   | 'profile_questions'
   | 'signup_started'
   | 'signup_submitted'
   | 'email_verified'
   | 'pricing_viewed'
   | 'checkout_started';

export interface ClientAcquisition extends AcquisitionAttribution {
   promo?: {
      code: string;
      percent_off: number;
      first_use_only?: boolean;
      expires_at?: string;
      status: 'active' | 'redeemed' | 'expired' | 'invalid' | 'retired';
   };
   current_step?: string;
   status?: string;
   milestones?: { first_paid_at?: number };
   promo_history?: NonNullable<ClientAcquisition['promo']>[];
   redemptions?: Array<AcquisitionAttribution & {
      anonymous_id: string;
      attached_at: number;
      outcome: 'applied' | 'ineligible' | 'invalid' | 'no_offer';
      promo?: ClientAcquisition['promo'];
   }>;
}

const CLICK_IDS = new Set(['gclid', 'gbraid', 'wbraid', 'fbclid']);
const safeValue = (value: unknown): value is string =>
   typeof value === 'string' && new TextEncoder().encode(value).length <= 512 && !value.includes('@') && !value.includes('://') && !Array.from(value).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);

export function sanitizeMarketingParameters(params: URLSearchParams) {
   const utm: Record<string, string> = {};
   const click_ids: Record<string, string> = {};
   for (const [key, value] of params) {
      if (!safeValue(value)) continue;
      if (/^utm_[a-zA-Z0-9_]{1,60}$/.test(key) && Object.keys(utm).length < 32) {
         utm[key.toLowerCase()] = value;
      } else if (CLICK_IDS.has(key)) {
         click_ids[key] = value;
      }
   }
   return { utm, click_ids };
}

export function sanitizeTouch(value: unknown, now = Date.now()): AcquisitionTouch | undefined {
   if (!value || typeof value !== 'object') return;
   const touch = value as Partial<AcquisitionTouch>;
   const timestamp = typeof touch.captured_at === 'number' ? touch.captured_at * 1000 : NaN;
   if (!Number.isFinite(timestamp) || timestamp > now + 60000 || now - timestamp > ACQUISITION_TTL) return;
   const params = new URLSearchParams();
   for (const record of [touch.utm, touch.click_ids]) {
      if (!record || typeof record !== 'object') continue;
      for (const [key, item] of Object.entries(record)) {
         if (safeValue(item)) params.set(key, item);
      }
   }
   const marketing = sanitizeMarketingParameters(params);
   const landing_path = typeof touch.landing_path === 'string' &&
      touch.landing_path.startsWith('/') && touch.landing_path.length <= 512
      ? touch.landing_path.split(/[?#]/)[0] : '/';
   const referrer_host = typeof touch.referrer_host === 'string' &&
      /^[a-zA-Z0-9.-]{1,253}$/.test(touch.referrer_host) ? touch.referrer_host : '';
   return { ...marketing, landing_path, referrer_host, captured_at: Math.floor(timestamp / 1000) };
}

export function captureTouch(url: URL, referrer = '', now = Date.now()): AcquisitionTouch {
   let referrer_host = '';
   try { referrer_host = new URL(referrer).hostname; } catch { /* No external referrer. */ }
   return {
      ...sanitizeMarketingParameters(url.searchParams),
      landing_path: url.pathname.slice(0, 512),
      referrer_host,
      captured_at: Math.floor(now / 1000),
   };
}

export function mergeAttribution(
   stored: AcquisitionAttribution,
   incoming: AcquisitionAttribution,
   now = Date.now(),
): AcquisitionAttribution {
   const first = sanitizeTouch(stored?.first_touch, now);
   const importedFirst = sanitizeTouch(incoming?.first_touch, now);
   const last = sanitizeTouch(stored?.last_touch, now);
   const importedLast = sanitizeTouch(incoming?.last_touch, now);
   const first_touch = !first || (importedFirst && importedFirst.captured_at <= first.captured_at)
      ? importedFirst : first;
   const meaningful = (touch?: AcquisitionTouch) => !!touch &&
      (Object.keys(touch.utm).length > 0 || Object.keys(touch.click_ids).length > 0);
   const last_touch = !last || (importedLast && (
      meaningful(importedLast) && !meaningful(last) ||
      meaningful(importedLast) === meaningful(last) && importedLast.captured_at >= last.captured_at
   ))
      ? importedLast : last;
   return { first_touch, last_touch };
}

export function stepForRoute(name: unknown): AcquisitionStep | undefined {
   const route = String(name || '');
   if (route === 'auth-client-register-coverage') return 'coverage_started';
   if (route === 'auth-client-register-therapy-type') return 'therapy_type';
   if (route === 'auth-client-signup') return 'signup_started';
   if (route === 'client-packages') return 'pricing_viewed';
   if (/^auth-client-register-(therapy-reasons|age-groups|client-gender|parent-verified|therapist-preference)$/.test(route)) return 'profile_questions';
}

export function analyticsPage(url: URL, referrer = '') {
   let safeReferrer = '';
   try { safeReferrer = new URL(referrer).origin; } catch { /* No external referrer. */ }
   return {
      path: url.pathname,
      url: `${url.origin}${url.pathname}`,
      search: '',
      referrer: safeReferrer,
   };
}
